import React from 'react';
import { Link } from 'react-router-dom';
import {
  INQUIRY_KAKAO_CHANNEL_URL,
  handleKakaoChatClick,
} from '../../domain/inquiry/PHONE';
import { DEFAULT_CONTACT_PHONE, type ContactPhone } from '../../domain/inquiry/resolveContactPhone';
import { PriceTag } from './PriceTag';

/**
 * ContactStickyCard — 상세 페이지 우측 sticky 문의 카드 (lg+ 전용).
 *
 * 시안: wireframes/concept-a-warm/detail-rich.html .r-contact-card (lg+, rich variant)
 *      wireframes/concept-a-warm/detail-sparse.html .r-contact-card (lg+, sparse variant)
 *
 * variant:
 *   - 'rich'   : 자료 풍부 케이스 — 전화/카톡 CTA + 미니 KV 4행 + 비슷한 단지 1개
 *   - 'sparse' : 자료 부족 케이스 — 알림 신청 CTA + 1533-8124 ghost + 확인된 항목 4행
 *
 * 정책 강제:
 *   - 가격 미노출 (PriceTag는 "가격 문의" 출력)
 *   - 전화번호는 phone prop(resolveContactPhone 결과) — 미지정 시 대표번호 1533-8124 (v2 2026-09-21)
 *   - 외부 도메인 링크 없음 (카톡 채널은 정책 허용)
 */

export type ContactStickyVariant = 'rich' | 'sparse';

export interface ContactStickyMiniRow {
  key: string;
  value: React.ReactNode;
  /** dashed 점선 + muted 처리 (확인 중 항목) */
  pending?: boolean;
}

export interface SimilarPropertyHint {
  id: number | string;
  title: string;
  locationLabel?: string;
  imageUrl?: string | null;
  href: string;
}

export interface ContactStickyCardProps {
  variant: ContactStickyVariant;
  /** 단지명 */
  propertyTitle: string;
  /** 미니 KV 4행 (rich: 확인된 정보, sparse: 확인된 정보 일부 또는 알림 안내) */
  miniRows?: ContactStickyMiniRow[];
  /** 비슷한 단지 (rich), 또는 자료 풍부한 인근 단지 (sparse) */
  similar?: SimilarPropertyHint[];
  /** @deprecated 2026-06-08 sparse 현장도 전화/카톡 2버튼으로 통일되어 더 이상 사용하지 않음 */
  onNotifyClick?: () => void;
  /** 최저 분양가 (만원). PriceTag 에 전달. */
  minPrice?: number | null;
  /** 최고 분양가 (만원). */
  maxPrice?: number | null;
  /** 분양가 라벨 — '원분양가' | '할인가' (v2b 2026-09-21). 미지정 시 '분양가' */
  priceLabel?: string;
  /** 분양가 아래 보조 줄 (예: '평당 1,640만') — v2h */
  priceSub?: string;
  /** 전화번호·전화문의·카톡상담 버튼만 숨김 (사이드 현장용, 사용자 피드백 2026-05-22).
   *  기본 레이아웃·단지명·분양가·미니 KV·유사 단지는 그대로 표시. */
  hideContact?: boolean;
  /** 전화번호 — resolveContactPhone(property). 미지정 시 대표번호 */
  phone?: ContactPhone;
  className?: string;
}

const PhoneIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ChatIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const MiniKVTable: React.FC<{ rows: ContactStickyMiniRow[]; title?: string }> = ({ rows, title }) => (
  <div className="mt-4 border-y border-ca-line py-3">
    {title && (
      <div className="mb-2 text-[10.5px] font-extrabold uppercase text-muted">
        {title}
      </div>
    )}
    <dl className="flex flex-col gap-1.5 text-[12.5px]">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <dt className="shrink-0 text-muted">{r.key}</dt>
          <dd className={`num min-w-0 truncate text-right font-bold ${r.pending ? 'text-mute2 italic' : 'text-ink'}`}>
            {r.pending ? (
              <span className="inline-flex items-center rounded-ca-sm border border-dashed border-ca-line-strong px-1.5 py-0.5 text-[11px] font-bold text-mute2">
                확인 중
              </span>
            ) : (
              r.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

const SimilarList: React.FC<{ items: SimilarPropertyHint[]; title?: string }> = ({ items, title }) => (
  <div className="border-t border-ca-line pt-3.5">
    {title && (
      <div className="mb-2 text-[10.5px] font-extrabold uppercase text-muted">
        {title}
      </div>
    )}
    <div className="flex flex-col gap-2">
      {items.map((s) => (
        <Link
          key={s.id}
          to={s.href}
          className="flex items-center gap-2.5 rounded-ca-md border border-ca-line bg-frame px-2.5 py-2 hover:border-ca-line-strong"
        >
          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-ca-sm bg-block">
            {s.imageUrl && (
              <img src={s.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-extrabold tracking-tight text-ink line-clamp-1">
              {s.title}
            </div>
            {s.locationLabel && (
              <div className="text-[11px] text-muted line-clamp-1">{s.locationLabel}</div>
            )}
          </div>
        </Link>
      ))}
    </div>
  </div>
);

export const ContactStickyCard: React.FC<ContactStickyCardProps> = ({
  variant,
  propertyTitle,
  miniRows = [],
  similar = [],
  minPrice,
  maxPrice,
  priceLabel = '분양가',
  priceSub,
  hideContact = false,
  phone = DEFAULT_CONTACT_PHONE,
  className = '',
}) => {
  // v2h(2026-09-22) 목업 PC 04 우측 카드 순서: 분양가(크게) → 요약 행 → 전화 버튼(번호 포함) → 카톡 → 상담 시간.
  // 단지명은 히어로에 있으므로 카드에서는 스크린리더용으로만 둔다. 사이드 현장(hideContact)은 버튼·시간 안내 숨김.
  const phoneCaption = phone.isDefault ? '전화 상담' : '현장 직통';
  const hours = '상담 가능 월~일 09:00 ~ 22:00 · 채팅상담 가능';
  const kvSectionTitle = variant === 'rich' ? undefined : '지금 자료 확인된 항목';
  const similarSectionTitle = variant === 'rich' ? '비슷한 단지' : '자료 풍부한 인근 단지';

  return (
    <div className={`rounded-ca-3xl bg-frame p-5 shadow-ca-card ${className}`}>
      <h3 className="sr-only">{propertyTitle} 분양 문의</h3>

      {/* 분양가 — minPrice/maxPrice 있으면 실제 가격, 없으면 "가격 문의" 폴백 */}
      <div className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-muted">{priceLabel}</span>
        <PriceTag size="lg" minPrice={minPrice} maxPrice={maxPrice} className="num !text-accent" />
        {priceSub && <span className="num text-[12.5px] text-muted">{priceSub}</span>}
      </div>

      {miniRows.length > 0 && <MiniKVTable rows={miniRows} title={kvSectionTitle} />}

      {!hideContact && (
        <>
          <div className="mt-4 flex flex-col gap-2">
            <a
              href={phone.tel}
              aria-label={`${phoneCaption} ${phone.display}`}
              className="flex h-[50px] items-center justify-center gap-2 rounded-ca-xl bg-accent px-4 text-[15px] font-extrabold tracking-tight text-white shadow-ca-cta active:translate-y-[1px]"
            >
              <PhoneIcon size={16} />
              {phoneCaption} <span className="num">{phone.display}</span>
            </a>
            <a
              href={INQUIRY_KAKAO_CHANNEL_URL}
              target="_blank"
              rel="noopener"
              referrerPolicy="unsafe-url"
              onClick={handleKakaoChatClick}
              className="flex h-11 items-center justify-center gap-2 rounded-ca-xl bg-ink px-4 text-[13.5px] font-extrabold tracking-tight text-white active:translate-y-[1px]"
            >
              <ChatIcon />
              카톡 상담
            </a>
          </div>
          <p className="mt-2.5 text-center text-[11px] text-mute2">{hours}</p>
        </>
      )}

      {similar.length > 0 && <SimilarList items={similar} title={similarSectionTitle} />}
    </div>
  );
};

export default ContactStickyCard;
